import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Container,
  Skeleton,
} from '@mui/material';
import {
  LinkedIn,
  Phone as PhoneIcon,
  Share as ShareIcon,
  Call as CallIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { TitleCompanyStack } from '../components/TitleCompanyStack';

// Normalize card data (flat or nested personalInfo)
const getField = (cardData, flat, nested) => {
  const v = cardData?.[flat];
  if (v != null && v !== '') return v;
  return nested ? cardData?.personalInfo?.[nested] : null;
};

const PublicCard = () => {
  const { cardUrl } = useParams();
  const [card, setCard] = useState(null);
  const [viewsThisWeek, setViewsThisWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [cardRes, viewsRes] = await Promise.all([
          api.get(`/cards/public/${cardUrl}`),
          api.get(`/analytics/card-views-public/${cardUrl}`).catch(() => ({ data: { viewsThisWeek: 0 } })),
        ]);
        if (cancelled) return;
        setCard(cardRes.data?.card || cardRes.data);
        setViewsThisWeek(viewsRes.data?.viewsThisWeek ?? 0);
      } catch (e) {
        if (!cancelled) setError('Card not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [cardUrl]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', py: 4 }}>
        <Container maxWidth="sm" sx={{ px: 2 }}>
          <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 1, mb: 2 }} />
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
        </Container>
      </Box>
    );
  }

  if (error || !card) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <Typography variant="h6" color="error">Card not found</Typography>
      </Box>
    );
  }

  const cardData = card.cardData || card.card_data || {};
  const name = getField(cardData, 'name') || [getField(cardData, null, 'firstName'), getField(cardData, null, 'lastName')].filter(Boolean).join(' ') || 'Unknown';
  const title = getField(cardData, 'title') || cardData.personalInfo?.title || '';
  const location = getField(cardData, 'company') || cardData.additionalInfo?.address || [cardData.city, cardData.country].filter(Boolean).join(', ') || '';
  const phone = getField(cardData, 'phone') || cardData.personalInfo?.phone || '';
  const email = getField(cardData, 'email') || cardData.personalInfo?.email || '';
  const profileImageUrl = cardData.profileImageUrl || '';
  const linkedin = cardData.linkedin || cardData.socialMedia?.linkedin || cardData.socialLinks?.find(l => l.platform === 'linkedin')?.url || '';
  const website = cardData.website || cardData.personalInfo?.website || '';

  const initials = name.split(/\s+/).map(s => s[0]).join('').toUpperCase().slice(0, 2) || '?';

  const handleCall = () => { if (phone) window.location.href = `tel:${phone}`; };
  const handleWhatsApp = () => { if (phone) window.open(`https://wa.me/${phone.replace(/\D/g, '')}`, '_blank'); };
  const handleLinkedIn = () => { if (linkedin) window.open(linkedin, '_blank'); };
  const handleShare = async () => {
    try {
      await navigator.share?.({
        title: name,
        text: `${name} - ${title}`,
        url: window.location.href,
      });
    } catch {
      navigator.clipboard?.writeText(window.location.href);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', py: 3, px: 2 }}>
      <Container maxWidth="sm" disableGutters sx={{ maxWidth: 420 }}>
        {/* Card container - slight rounded like screenshot */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}
        >
          {/* Dark header - decorative circles, avatar, name, subtitle, pill-style contact buttons */}
          <Box
            sx={{
              background: '#102E5C',
              color: 'white',
              p: 3,
              position: 'relative',
              overflow: 'hidden',
              borderBottomLeftRadius: 1,
              borderBottomRightRadius: 1,
            }}
          >
            {/* Subtle decorative circles (upper right) */}
            <Box sx={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.12)' }} />
            <Box sx={{ position: 'absolute', top: -20, right: 40, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.08)' }} />
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, position: 'relative' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: '#00A7E3',
                  color: 'white',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  overflow: 'hidden',
                }}
              >
                {profileImageUrl ? (
                  <Box component="img" src={profileImageUrl} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  initials
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', lineHeight: 1.2 }}>
                  {name}
                </Typography>
                <TitleCompanyStack
                  title={title}
                  company={location}
                  boxSx={{ mt: 0.5 }}
                  titleSx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.85)', lineHeight: 1.35 }}
                  companySx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.78)', lineHeight: 1.35 }}
                />
              </Box>
            </Box>
            {/* Contact pills - pill shape, light fill, dark text (reference design) */}
            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              {phone && (
                <Button
                  size="small"
                  startIcon={<CallIcon sx={{ fontSize: 18 }} />}
                  onClick={handleCall}
                  sx={{
                    borderRadius: '50px',
                    bgcolor: 'rgba(255,255,255,0.22)',
                    color: '#1e293b',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.75,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.32)' },
                  }}
                >
                  Call
                </Button>
              )}
              {phone && (
                <Button
                  size="small"
                  startIcon={<span style={{ fontSize: 18 }}>💬</span>}
                  onClick={handleWhatsApp}
                  sx={{
                    borderRadius: '50px',
                    bgcolor: 'rgba(255,255,255,0.22)',
                    color: '#1e293b',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.75,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.32)' },
                  }}
                >
                  WhatsApp
                </Button>
              )}
              {linkedin && (
                <Button
                  size="small"
                  startIcon={<LinkedIn sx={{ fontSize: 18 }} />}
                  onClick={handleLinkedIn}
                  sx={{
                    borderRadius: '50px',
                    bgcolor: 'rgba(255,255,255,0.22)',
                    color: '#1e293b',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.75,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.32)' },
                  }}
                >
                  LinkedIn
                </Button>
              )}
            </Box>
          </Box>

          <CardContent sx={{ p: 2, bgcolor: 'background.paper' }}>
            {/* Save Contact + Share - accent blue primary, outlined Share */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PhoneIcon />}
                href={`tel:${phone}`}
                component="a"
                sx={{
                  borderRadius: 1,
                  fontWeight: 600,
                  textTransform: 'none',
                  py: 1.25,
                  bgcolor: '#00A7E3',
                  '&:hover': { bgcolor: '#0080B8' },
                }}
              >
                Save Contact
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={handleShare}
                sx={{
                  borderRadius: 1,
                  fontWeight: 600,
                  textTransform: 'none',
                  py: 1.25,
                  borderColor: '#00A7E3',
                  color: '#00A7E3',
                  '&:hover': { borderColor: '#0080B8', color: '#0080B8', bgcolor: 'rgba(0,167,227,0.06)' },
                }}
              >
                Share
              </Button>
            </Box>

            {/* THIS WEEK stat card - view count with accent blue "↑ Card Views" */}
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50',
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.5 }}>
                THIS WEEK
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {viewsThisWeek != null ? viewsThisWeek : '—'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#00A7E3', fontWeight: 500 }}>
                  ↑ Card Views
                </Typography>
              </Box>
            </Box>

            {/* Optional: email / website links */}
            {(email || website) && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                {email && (
                  <Typography variant="body2" component="a" href={`mailto:${email}`} sx={{ color: 'primary.main', display: 'block', textDecoration: 'none' }}>
                    {email}
                  </Typography>
                )}
                {website && (
                  <Typography variant="body2" component="a" href={website.startsWith('http') ? website : `https://${website}`} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main', display: 'block', textDecoration: 'none', mt: 0.5 }}>
                    {website}
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', mt: 2 }}>
          Powered by WebCard Digital
        </Typography>
      </Container>
    </Box>
  );
};

export default PublicCard;
